import React, { useMemo } from 'react';
import { GridReactContextProvider } from '../../../Utils/DataGridWrapper/GridContext';
import { CourseAttendanceTable } from './CourseAttendanceTable/CourseAttendanceTable';
import { useUserAdminOrgs } from '../../Hooks/UseUserAdminOrgs';
import { i18n } from '../../../Utils/i18n/i18n';
import { get, flatMap, uniqBy } from 'lodash';
import './CourseAttendance.scss';
import LoadingSpinner from '../../LoadingSpinner/LoadingSpinner';

/**
 * Container component for Course Attendance with organization context
 */
export const CourseAttendance = () => {
    const {
        adminOrganizationOptions,
        adminOrganizations,
        isOrgsLoading
    } = useUserAdminOrgs({
        withAllOption: true,
        withCoursesAndSignDocs: true,
        includePersonalOrgs: true // VPO: Include personal orgs so courses assigned only to VPO users appear in filters
    });

    // Extract course SignDocs (FRONTAL_COURSE and ONLINE_COURSE) from all organizations
    const signDocsOptions = useMemo(() => {
        if (!adminOrganizations) return [];

        // Get all SignDocs from all organizations
        const allSignDocs = flatMap(adminOrganizations, (org) =>
            get(org, 'organization.signDocs', [])
        );

        // Filter only course types (FRONTAL_COURSE and ONLINE_COURSE)
        const courseSignDocs = allSignDocs.filter((signDoc) => {
            const type = get(signDoc, 'type', '');
            return type === 'FRONTAL_COURSE' || type === 'ONLINE_COURSE';
        });

        // Remove duplicates and map to options format
        const uniqueCourses = uniqBy(courseSignDocs, 'id');

        const options = uniqueCourses.map((signDoc) => ({
            value: signDoc.id,
            text: signDoc.docName
        }));

        // Add "All Courses" option at the beginning
        return [
            { value: 'all', text: i18n.translate('courseAttendance.filters.allCourses') },
            ...options
        ];
    }, [adminOrganizations]);

    if (isOrgsLoading) {
        return <LoadingSpinner />;
    }

    return (
        <GridReactContextProvider>
            <div className="course-attendance-container">
                <CourseAttendanceTable
                    organizationsOptions={adminOrganizationOptions}
                    adminOrganizations={adminOrganizations}
                    signDocsOptions={signDocsOptions}
                />
            </div>
        </GridReactContextProvider>
    );
};
